import React, { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
} from "framer-motion";
import EventCard from "./EventCard";

/**
 * PhaseSection — sticky vertical parallax, refined.
 *
 * Layout:
 *   section.height = (events + 1) * 100vh
 *   sticky h-screen — pins for events * 100vh
 *   events layer with margin-top:-100vh:
 *     - 1 breathing screen (sticky title + bg alone)
 *     - N event screens (each h-screen scroll-snap-align start)
 *   Content ends exactly at section bottom — no trailing gap.
 *
 * Parallax:
 *   - Background: scale 1.08 → 1.25, y 0% → -18% (bigger drift)
 *   - Watermark:  x 5% → -30% (wide horizontal travel)
 *   - Title block: y 0 → -80px (gentle float-away)
 *   - Each transform passed through useSpring for buttery motion.
 */
const PhaseSection = ({ phase, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  // Smooth the raw progress first, then derive transforms
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 28,
    mass: 0.35,
    restDelta: 0.001,
  });

  const bgScale = useTransform(smoothProgress, [0, 1], [1.08, 1.28]);
  const bgY = useTransform(smoothProgress, [0, 1], ["0%", "-18%"]);
  const watermarkX = useTransform(
    smoothProgress,
    [0, 1],
    [isOdd(index) ? "10%" : "-10%", isOdd(index) ? "-25%" : "25%"]
  );
  const watermarkY = useTransform(smoothProgress, [0, 1], ["0%", "-8%"]);
  // Chrome (phase title block, BAB indicator, intro quote, watermark) is
  // visible only on the intro screen. Once the user starts reading event
  // cards, chrome fades out so the card becomes the sole focus.
  const chromeOpacity = useTransform(
    smoothProgress,
    [0, 0.05, 0.16, 0.85, 0.95, 1],
    [1, 1, 0, 0, 0.2, 0.4]
  );
  const titleY = useTransform(smoothProgress, [0, 0.2, 1], [0, -60, -160]);
  // Watermark fades with chrome but stays a touch longer for atmosphere.
  const watermarkOpacity = useTransform(
    smoothProgress,
    [0, 0.05, 0.22, 0.78, 0.95, 1],
    [1, 1, 0, 0, 0.25, 0.45]
  );
  const overlayOpacity = useTransform(
    smoothProgress,
    [0, 0.15, 0.85, 1],
    [0.38, 0.5, 0.55, 0.68]
  );

  return (
    <section
      ref={ref}
      id={`fase-${phase.id}`}
      data-testid={`phase-${phase.id}-section`}
      data-phase-index={index}
      className="relative w-full bg-ink phase-section"
      style={{ height: `${(phase.events.length + 1) * 100}vh` }}
    >
      {/* === Sticky background layer === */}
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        <motion.div
          style={{ scale: bgScale, y: bgY }}
          className="absolute inset-0"
        >
          <img
            src={phase.image}
            alt={phase.title}
            className="h-full w-full object-cover"
            loading={index < 2 ? "eager" : "lazy"}
            draggable={false}
          />
        </motion.div>

        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-ink"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/20 via-transparent to-ink/90 pointer-events-none" />
        <div className="absolute inset-0 grain pointer-events-none" />

        {/* Watermark — wide horizontal parallax, fades when reading cards */}
        <motion.div
          style={{ x: watermarkX, y: watermarkY, opacity: watermarkOpacity }}
          data-testid={`phase-${phase.id}-watermark`}
          className={`absolute top-1/2 -translate-y-1/2 watermark text-[14rem] sm:text-[20rem] md:text-[28rem] lg:text-[34rem] xl:text-[38rem] ${
            isOdd(index) ? "right-[-6%]" : "left-[-6%]"
          }`}
        >
          {phase.watermark}
        </motion.div>

        {/* Phase title + intro — visible only on the intro screen, then
            fades out so cards become the sole focus. */}
        <motion.div
          style={{ y: titleY, opacity: chromeOpacity }}
          data-testid={`phase-${phase.id}-chrome`}
          className="absolute inset-0 px-6 md:px-12 lg:px-20 flex flex-col justify-between py-10 md:py-16 z-10 pointer-events-none"
        >
          <div className="flex items-start justify-between gap-6">
            <div className="max-w-2xl">
              <span
                data-testid={`phase-${phase.id}-number`}
                className="block text-[11px] uppercase tracking-[0.4em] text-copper mb-3"
              >
                {phase.phaseNumber} — {phase.period}
              </span>
              <h2
                data-testid={`phase-${phase.id}-title`}
                className="font-serif text-parchment leading-[0.95] tracking-tightest text-4xl md:text-6xl lg:text-7xl"
              >
                {phase.title}
              </h2>
              <p className="font-serif italic text-copper text-lg md:text-2xl mt-3 font-light">
                {phase.subtitle}
              </p>
            </div>

            <div className="hidden md:flex flex-col items-end gap-1 text-right shrink-0">
              <span className="text-[10px] uppercase tracking-[0.3em] text-warm-muted">
                Bab
              </span>
              <span className="font-serif text-parchment text-3xl">
                0{index + 1}
                <span className="text-warm-muted">/06</span>
              </span>
            </div>
          </div>

          <div className="max-w-2xl">
            <div className="hair mb-5" />
            <p className="font-serif italic text-parchment-soft text-base md:text-xl leading-relaxed font-light">
              "{phase.intro}"
            </p>
          </div>
        </motion.div>
      </div>

      {/* === Scrolling event cards (layer after sticky bg, same flow) === */}
      <div className="relative z-20 pointer-events-none">
        {phase.events.map((event, i) => (
          <div
            key={`${phase.id}-${i}`}
            className="h-screen w-full flex items-center px-6 md:px-12 lg:px-20 snap-start"
            data-snap="true"
            data-event-index={i}
          >
            <EventCard
              event={event}
              index={i}
              phaseId={phase.id}
              alignRight={i % 2 === 1}
              total={phase.events.length}
              progress={smoothProgress}
              eventIndex={i}
              totalEvents={phase.events.length}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

function isOdd(n) {
  return n % 2 === 1;
}

export default PhaseSection;
